import { createChartIcon } from "./chart-icon.js";
import { FloatingWindowController } from "../workspace/floating-window-controller.js";
import {
  findNode,
  flattenVisibleObjectTree
} from "../workspace/object-tree-model.js";

const DEFAULT_WIDTH = 360;
const DEFAULT_HEIGHT = 560;
const STORAGE_VERSION = 1;

export class ObjectTreeWindow {
  constructor({
    surface,
    launcher,
    registry,
    storageKey,
    restoreOnConstruct = true,
    onStateChange = () => {},
    announce = () => {}
  }) {
    if (!(surface instanceof HTMLElement)) {
      throw new TypeError("ObjectTreeWindow requires a workspace surface.");
    }
    if (!(launcher instanceof HTMLButtonElement)) {
      throw new TypeError("ObjectTreeWindow requires a launcher button.");
    }
    this.surface = surface;
    this.launcher = launcher;
    this.registry = registry;
    this.storageKey = storageKey;
    this.onStateChange = onStateChange;
    this.announce = announce;
    this.expandedIds = new Set();
    this.selectedId = null;
    this.query = "";
    this.opened = false;
    this.externalSelectedId = null;
    this.pendingActions = new Set();
    this.registrySubscription = null;
    this.renderPending = false;
    this.savedState = readState(storageKey);
    this.expandedInitialized = false;
    this.abortController = new AbortController();
    this.element = this.createElement();
    this.tree = this.element.querySelector("[data-object-tree]");
    this.search = this.element.querySelector("[data-object-tree-search]");
    this.empty = this.element.querySelector("[data-object-tree-empty]");
    this.floating = new FloatingWindowController({
      surface,
      windowElement: this.element,
      dragHandle: this.element.querySelector("[data-object-tree-drag-handle]"),
      resizeHandles: this.element.querySelectorAll("[data-window-resize]"),
      minimumWidth: 300,
      minimumHeight: 280,
      onInteractionEnd: () => this.saveState()
    });
    this.element.hidden = true;
    this.bind();
    if (restoreOnConstruct && this.savedState.open) this.open({ restoreFocus: false });
  }

  setStorageKey(storageKey) {
    const next = String(storageKey || "").trim();
    if (!next) throw new TypeError("Object Tree storage requires a product-qualified key.");
    this.storageKey = next;
    this.savedState = readState(next);
    this.expandedIds = new Set(
      Array.isArray(this.savedState.expanded) ? this.savedState.expanded : []
    );
    this.expandedInitialized = false;
  }

  createElement() {
    const element = document.createElement("section");
    element.className = "chart-floating-window object-tree-window";
    element.dataset.objectTreeWindow = "";
    element.dataset.windowSnapId = "object-tree";
    element.setAttribute("role", "dialog");
    element.setAttribute("aria-label", "Object Tree");
    element.innerHTML = `
      <header class="chart-window-titlebar object-tree-titlebar" data-object-tree-drag-handle>
        <div>
          <span class="object-tree-title-icon" aria-hidden="true">${treeIconMarkup()}</span>
          <strong>Object Tree</strong>
        </div>
        <button type="button" data-object-tree-close aria-label="Close Object Tree" title="Close">&times;</button>
      </header>
      <label class="object-tree-search">
        <span class="chart-sr-only">Search workspace objects</span>
        <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6.5"></circle><path d="m16 16 4 4"></path></svg>
        <input type="search" data-object-tree-search placeholder="Search objects&hellip;" autocomplete="off" />
      </label>
      <div class="object-tree-scroll">
        <div class="object-tree"
             data-object-tree
             role="tree"
             aria-label="Workspace objects"
             aria-description="Use arrow keys to navigate, Enter to activate, F2 for settings, and Delete to remove an available object."></div>
        <div class="object-tree-empty" data-object-tree-empty role="status" hidden>
          <strong>No objects found</strong>
          <span>Try another search.</span>
        </div>
      </div>
      <div class="object-tree-live chart-sr-only" data-object-tree-live aria-live="polite"></div>
      ${resizeHandles()}
    `;
    this.surface.append(element);
    return element;
  }

  bind() {
    const options = { signal: this.abortController.signal };
    this.launcher.addEventListener("click", () => this.open(), options);
    this.element.querySelector("[data-object-tree-close]")
      .addEventListener("click", () => this.close(), options);
    this.search.addEventListener("input", () => {
      this.query = this.search.value;
      this.scheduleRender();
    }, options);
    this.search.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      if (this.search.value) {
        this.search.value = "";
        this.query = "";
        this.scheduleRender({ immediate: true });
      } else {
        this.close();
      }
    }, options);
    this.tree.addEventListener("click", (event) => this.handleClick(event), options);
    this.tree.addEventListener("contextmenu", (event) => this.handleContextMenu(event), options);
    this.tree.addEventListener("keydown", (event) => this.handleKeydown(event), options);
    this.element.addEventListener("pointerdown", () => this.focus(), {
      ...options,
      capture: true
    });
  }

  open({ restoreFocus = true } = {}) {
    if (this.opened) {
      this.focus();
      this.floating.constrainToSurface();
      if (restoreFocus) this.search.focus({ preventScroll: true });
      return;
    }
    this.element.hidden = false;
    this.opened = true;
    this.launcher.setAttribute("aria-pressed", "true");
    if (!this.registrySubscription) {
      this.registrySubscription = this.registry.subscribe(() => this.scheduleRender());
      this.registry.connect();
    }
    const geometry = validGeometry(this.savedState.geometry)
      ? this.savedState.geometry
      : this.defaultGeometry();
    this.floating.applyGeometry(geometry);
    this.floating.constrainToSurface();
    this.focus();
    this.scheduleRender({ immediate: true });
    this.saveState();
    if (restoreFocus) this.search.focus({ preventScroll: true });
    this.announce("Object Tree opened.");
  }

  close({ restoreFocus = true } = {}) {
    if (!this.opened) return;
    this.opened = false;
    this.element.hidden = true;
    this.element.classList.remove("is-focused");
    this.launcher.setAttribute("aria-pressed", "false");
    this.registrySubscription?.();
    this.registrySubscription = null;
    this.registry.disconnect();
    this.saveState();
    if (restoreFocus) this.launcher.focus({ preventScroll: true });
    this.announce("Object Tree closed.");
  }

  focus() {
    if (!this.opened) return;
    this.element.classList.add("is-focused");
  }

  scheduleRender({ immediate = false } = {}) {
    if (!this.opened) return;
    if (immediate) {
      this.renderPending = false;
      this.render();
      return;
    }
    if (this.renderPending) return;
    this.renderPending = true;
    requestAnimationFrame(() => {
      this.renderPending = false;
      if (this.opened) this.render();
    });
  }

  render() {
    const focusedId = document.activeElement?.closest?.("[data-object-tree-id]")
      ?.dataset.objectTreeId;
    const nodes = this.registry.snapshot({ query: this.query });
    const searchActive = this.query.trim().length > 0;
    this.seedExpandedNodes(nodes);
    const selectedPath = searchActive ? null : findSelectedNodePath(nodes);
    const externalSelectedId = selectedPath?.at(-1)?.id || null;
    const revealExternalSelection = Boolean(
      externalSelectedId && externalSelectedId !== this.externalSelectedId
    );
    if (externalSelectedId) {
      this.externalSelectedId = externalSelectedId;
      this.selectedId = externalSelectedId;
      for (const ancestor of selectedPath.slice(0, -1)) {
        this.expandedIds.add(ancestor.id);
      }
    } else if (
      !searchActive
      &&
      this.externalSelectedId
      && this.selectedId === this.externalSelectedId
    ) {
      this.selectedId = null;
      this.externalSelectedId = null;
    }
    const visible = flattenVisibleObjectTree(nodes, this.expandedIds, {
      searchActive
    });
    const existingRows = new Map(
      [...this.tree.querySelectorAll(":scope > [data-object-tree-id]")]
        .map((row) => [row.dataset.objectTreeId, row])
    );
    const fragment = document.createDocumentFragment();
    const frontmostId = nodes[0]?.id || null;
    for (const rowModel of visible) {
      const rowDescriptor = {
        ...rowModel,
        frontmost: rowModel.parentId === null && rowModel.node.id === frontmostId
      };
      const signature = rowRenderSignature(
        rowDescriptor,
        this.selectedId,
        this.expandedIds,
        this.query
      );
      const existing = existingRows.get(rowModel.node.id);
      const row = existing?.dataset.objectTreeSignature === signature
        ? existing
        : this.createRow(rowDescriptor);
      row.dataset.objectTreeSignature = signature;
      fragment.append(row);
    }
    this.tree.replaceChildren(fragment);
    this.empty.hidden = visible.length > 0;
    if (visible.length === 0) {
      this.empty.querySelector("strong").textContent = this.query.trim()
        ? "No objects found"
        : "Workspace is empty";
      this.empty.querySelector("span").textContent = this.query.trim()
        ? "Try another search."
        : "Open a chart or watchlist to begin.";
    }
    const focusId = focusedId || this.selectedId;
    const focusRow = focusId
      ? this.tree.querySelector(
          `[data-object-tree-id="${CSS.escape(focusId)}"]`
        )
      : null;
    const rovingRow = focusRow || this.tree.querySelector("[data-object-tree-id]");
    for (const row of this.tree.querySelectorAll("[data-object-tree-id]")) {
      row.tabIndex = row === rovingRow ? 0 : -1;
    }
    focusRow?.focus({ preventScroll: true });
    if (revealExternalSelection && !this.element.contains(document.activeElement)) {
      this.tree.querySelector(
        `[data-object-tree-id="${CSS.escape(externalSelectedId)}"]`
      )?.scrollIntoView({ block: "nearest" });
    }
    this.saveState();
  }

  createRow({ node, level, parentId, frontmost = false }) {
    const row = document.createElement("div");
    row.className = "object-tree-row";
    row.dataset.objectTreeId = node.id;
    row.dataset.objectTreeKind = node.kind;
    if (parentId) row.dataset.objectTreeParent = parentId;
    row.style.setProperty("--object-tree-level", String(level));
    row.setAttribute("role", "treeitem");
    row.setAttribute("aria-level", String(level));
    row.setAttribute("aria-selected", String(node.id === this.selectedId));
    row.tabIndex = node.id === this.selectedId ? 0 : -1;
    const expandable = node.children.length > 0;
    if (expandable) {
      row.setAttribute(
        "aria-expanded",
        String(this.query.trim().length > 0 || this.expandedIds.has(node.id))
      );
    }
    if (node.active) row.classList.add("is-active");
    if (frontmost) row.classList.add("is-frontmost");
    if (nodeMatchesQuery(node, this.query)) row.classList.add("is-search-match");
    if (node.hidden) row.classList.add("is-hidden");
    if (node.locked) row.classList.add("is-locked");
    if (node.paused) row.classList.add("is-paused");

    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = "object-tree-chevron";
    toggle.dataset.objectTreeToggle = "";
    toggle.tabIndex = -1;
    toggle.disabled = !expandable;
    toggle.setAttribute(
      "aria-label",
      expandable
        ? `${this.expandedIds.has(node.id) ? "Collapse" : "Expand"} ${node.label}`
        : ""
    );
    toggle.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 6 6 6-6 6"></path></svg>`;

    const icon = document.createElement("span");
    icon.className = "object-tree-kind-icon";
    icon.setAttribute("aria-hidden", "true");
    icon.innerHTML = objectIconMarkup(node.kind);

    const copy = document.createElement("span");
    copy.className = "object-tree-copy";
    const label = document.createElement("strong");
    label.textContent = node.label;
    copy.append(label);
    if (node.description || node.status || frontmost) {
      const detail = document.createElement("span");
      detail.textContent = [
        node.description,
        node.status,
        frontmost ? "Frontmost" : ""
      ].filter(Boolean).join(" · ");
      copy.append(detail);
    }

    const state = document.createElement("span");
    state.className = "object-tree-state";
    state.setAttribute("aria-hidden", "true");
    if (node.locked) state.append(statusBadge("lock", "Locked"));
    if (node.hidden) state.append(statusBadge("hidden", "Hidden"));
    if (node.paused) state.append(statusBadge("paused", "Paused"));
    if (frontmost) state.append(statusBadge("frontmost", "Frontmost window"));

    const actions = document.createElement("span");
    actions.className = "object-tree-actions";
    if (node.canSettings) {
      actions.append(actionButton("settings", `Settings for ${node.label}`));
    } else if (node.settingsDisabledReason) {
      actions.append(actionButton(
        "settings",
        `Settings for ${node.label}`,
        node.settingsDisabledReason
      ));
    }
    if (node.canDelete) {
      actions.append(actionButton("delete", `Delete ${node.label}`));
    } else if (node.deleteDisabledReason) {
      actions.append(actionButton(
        "delete",
        `Delete ${node.label}`,
        node.deleteDisabledReason
      ));
    }
    row.append(toggle, icon, copy, state, actions);
    row.title = [
      node.label,
      node.description,
      node.status,
      frontmost ? "Frontmost" : ""
    ].filter(Boolean).join(" · ");
    return row;
  }

  async handleClick(event) {
    const row = event.target.closest("[data-object-tree-id]");
    if (!row) return;
    const node = this.registry.resolve(row.dataset.objectTreeId);
    if (!node) return;
    const action = event.target.closest("[data-object-tree-action]")
      ?.dataset.objectTreeAction;
    if (action) {
      event.stopPropagation();
      await this.runNodeAction(node, action);
      return;
    }
    if (event.target.closest("[data-object-tree-toggle]")) {
      this.toggleNode(node.id);
      return;
    }
    await this.activateNode(node);
  }

  async handleContextMenu(event) {
    const row = event.target.closest("[data-object-tree-id]");
    if (!row) return;
    const node = this.registry.resolve(row.dataset.objectTreeId);
    if (!node?.canSettings) return;
    event.preventDefault();
    await this.selectNode(node);
    await this.runNodeAction(node, "settings");
  }

  async handleKeydown(event) {
    const row = event.target.closest("[data-object-tree-id]");
    if (!row) return;
    const rows = [...this.tree.querySelectorAll("[data-object-tree-id]")];
    const index = rows.indexOf(row);
    const node = this.registry.resolve(row.dataset.objectTreeId);
    if (!node) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const delta = event.key === "ArrowDown" ? 1 : -1;
      this.focusRow(rows[Math.max(0, Math.min(rows.length - 1, index + delta))]);
      return;
    }
    if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      this.focusRow(event.key === "Home" ? rows[0] : rows.at(-1));
      return;
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      if (node.children.length > 0 && !this.expandedIds.has(node.id)) {
        this.expandedIds.add(node.id);
        this.render();
      } else {
        this.focusRow(rows[index + 1]);
      }
      return;
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      if (this.expandedIds.has(node.id) && node.children.length > 0) {
        this.expandedIds.delete(node.id);
        this.render();
      } else {
        const parentId = row.dataset.objectTreeParent;
        if (parentId) {
          this.focusRow(this.tree.querySelector(
            `[data-object-tree-id="${CSS.escape(parentId)}"]`
          ));
        }
      }
      return;
    }
    if (event.key === "*" && node.children.length > 0) {
      event.preventDefault();
      const parentId = row.dataset.objectTreeParent;
      for (const candidate of rows) {
        if ((candidate.dataset.objectTreeParent || "") !== (parentId || "")) continue;
        const sibling = this.registry.resolve(candidate.dataset.objectTreeId);
        if (sibling?.children.length) this.expandedIds.add(sibling.id);
      }
      this.render();
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      await this.activateNode(node);
      return;
    }
    if (event.key === "F2" && node.canSettings) {
      event.preventDefault();
      await this.runNodeAction(node, "settings");
      return;
    }
    if (event.key === "Delete" && node.canDelete) {
      event.preventDefault();
      await this.runNodeAction(node, "delete");
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      if (this.query) {
        this.query = "";
        this.search.value = "";
        this.scheduleRender({ immediate: true });
        this.search.focus({ preventScroll: true });
      } else {
        this.close();
      }
    }
  }

  async activateNode(node) {
    await this.selectNode(node);
    if (node.canActivate) await node.activate();
    this.scheduleRender();
  }

  async selectNode(node) {
    this.selectedId = node.id;
    for (const row of this.tree.querySelectorAll("[data-object-tree-id]")) {
      const selected = row.dataset.objectTreeId === node.id;
      row.setAttribute("aria-selected", String(selected));
      row.tabIndex = selected ? 0 : -1;
    }
    this.tree.querySelector(
      `[data-object-tree-id="${CSS.escape(node.id)}"]`
    )?.focus({ preventScroll: true });
    this.saveState();
  }

  async runNodeAction(node, action) {
    const handler = action === "settings" ? node.settings : node.delete;
    if (typeof handler !== "function") return;
    const actionKey = `${node.id}:${action}`;
    if (this.pendingActions.has(actionKey)) return;
    this.pendingActions.add(actionKey);
    const actionButton = this.tree.querySelector(
      `[data-object-tree-id="${CSS.escape(node.id)}"] `
      + `[data-object-tree-action="${CSS.escape(action)}"]`
    );
    if (actionButton instanceof HTMLButtonElement) actionButton.disabled = true;
    try {
      await handler();
      this.registry.notify();
    } catch (error) {
      this.announce(error instanceof Error ? error.message : "The object action failed.");
    } finally {
      this.pendingActions.delete(actionKey);
      const currentButton = this.tree.querySelector(
        `[data-object-tree-id="${CSS.escape(node.id)}"] `
        + `[data-object-tree-action="${CSS.escape(action)}"]`
      );
      if (
        currentButton instanceof HTMLButtonElement
        && !currentButton.dataset.objectTreeDisabledReason
      ) {
        currentButton.disabled = false;
      }
    }
  }

  focusRow(row) {
    if (!(row instanceof HTMLElement)) return;
    for (const candidate of this.tree.querySelectorAll("[data-object-tree-id]")) {
      candidate.tabIndex = candidate === row ? 0 : -1;
    }
    row.focus({ preventScroll: true });
  }

  toggleNode(nodeId) {
    if (this.expandedIds.has(nodeId)) this.expandedIds.delete(nodeId);
    else this.expandedIds.add(nodeId);
    this.render();
    this.saveState();
  }

  seedExpandedNodes(nodes) {
    if (this.expandedInitialized) return;
    this.expandedInitialized = true;
    if (Array.isArray(this.savedState.expanded)) {
      this.expandedIds = new Set(this.savedState.expanded);
    }
    if (this.expandedIds.size === 0) {
      for (const node of nodes) this.expandedIds.add(node.id);
    }
  }

  defaultGeometry() {
    const width = Math.min(DEFAULT_WIDTH, this.surface.clientWidth);
    const height = Math.min(DEFAULT_HEIGHT, this.surface.clientHeight);
    return {
      x: Math.max(0, this.surface.clientWidth - width - 18),
      y: Math.max(0, Math.min(48, this.surface.clientHeight - height)),
      width,
      height
    };
  }

  saveState() {
    this.savedState = {
      version: STORAGE_VERSION,
      open: this.opened,
      geometry: this.opened ? this.floating.snapshot() : this.savedState.geometry,
      expanded: [...this.expandedIds]
    };
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.savedState));
    } catch {
      // The Object Tree remains usable when storage is unavailable.
    }
    this.onStateChange(this.snapshotState());
  }

  snapshotState() {
    return {
      open: this.opened,
      geometry: this.opened ? this.floating.snapshot() : this.savedState.geometry,
      expanded: [...this.expandedIds]
    };
  }

  restoreState(state) {
    if (!state || typeof state !== "object") return;
    const geometry = validGeometry(state.geometry) ? state.geometry : this.savedState.geometry;
    this.savedState = {
      version: STORAGE_VERSION,
      open: state.open === true,
      geometry,
      expanded: Array.isArray(state.expanded)
        ? state.expanded.filter((id) => typeof id === "string").slice(0, 500)
        : []
    };
    this.expandedIds = new Set(this.savedState.expanded);
    this.expandedInitialized = true;
    if (this.savedState.open) {
      if (!this.opened) this.open({ restoreFocus: false });
      else if (validGeometry(geometry)) this.floating.applyGeometry(geometry);
    } else if (this.opened) {
      this.close({ restoreFocus: false });
    }
  }

  dispose() {
    this.registrySubscription?.();
    this.registrySubscription = null;
    this.registry.disconnect();
    this.abortController.abort();
    this.floating.dispose();
    this.element.remove();
  }
}

function actionButton(action, label, disabledReason = "") {
  const button = document.createElement("button");
  button.type = "button";
  button.className = `object-tree-action is-${action}`;
  button.dataset.objectTreeAction = action;
  button.setAttribute("aria-label", label);
  button.title = disabledReason || (action === "settings" ? "Settings" : "Delete");
  if (disabledReason) {
    button.setAttribute("aria-disabled", "true");
    button.dataset.objectTreeDisabledReason = disabledReason;
    button.setAttribute("aria-description", disabledReason);
  }
  const icon = createChartIcon(
    action === "delete" ? "objectTrash" : "objectSettings"
  );
  if (icon) button.append(icon);
  return button;
}

function statusBadge(kind, label) {
  const badge = document.createElement("span");
  badge.className = `object-tree-status is-${kind}`;
  badge.title = label;
  badge.textContent = kind === "lock"
    ? "\u2022"
    : kind === "hidden"
      ? "\u25cb"
      : kind === "frontmost"
        ? "\u2191"
        : "\u2016";
  return badge;
}

function findSelectedNodePath(nodes, path = []) {
  for (const node of nodes || []) {
    const nextPath = [...path, node];
    if (node.selected) return nextPath;
    const nested = findSelectedNodePath(node.children, nextPath);
    if (nested) return nested;
  }
  return null;
}

function nodeMatchesQuery(node, query) {
  const normalizedQuery = String(query).trim().toLocaleLowerCase();
  if (!normalizedQuery) return false;
  return [
    node.label,
    node.description,
    node.kind,
    node.status
  ].some((value) => String(value || "").toLocaleLowerCase().includes(normalizedQuery));
}

function rowRenderSignature(
  { node, level, parentId, frontmost },
  selectedId,
  expandedIds,
  query
) {
  return JSON.stringify([
    node.id,
    node.kind,
    node.label,
    node.description,
    node.status,
    level,
    parentId,
    frontmost,
    node.id === selectedId,
    node.children.length > 0 && expandedIds.has(node.id),
    node.active,
    node.hidden,
    node.locked,
    node.paused,
    node.canSettings,
    node.canDelete,
    node.settingsDisabledReason,
    node.deleteDisabledReason,
    String(query).trim().length > 0,
    nodeMatchesQuery(node, query)
  ]);
}

function readState(storageKey) {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey) || "{}");
    return saved?.version === STORAGE_VERSION ? saved : {};
  } catch {
    return {};
  }
}

function validGeometry(value) {
  return value
    && ["x", "y", "width", "height"].every((key) => Number.isFinite(Number(value[key])));
}

function resizeHandles() {
  return ["n", "e", "s", "w", "ne", "nw", "se", "sw"]
    .map((edge) => `<span class="chart-resize-handle ${edge}" data-window-resize="${edge}" aria-hidden="true"></span>`)
    .join("");
}

function treeIconMarkup() {
  return `<svg viewBox="0 0 24 24"><path d="M6 4v16M6 7h5M6 16h5"></path><rect x="11" y="4" width="8" height="6" rx="1.5"></rect><rect x="11" y="13" width="8" height="6" rx="1.5"></rect></svg>`;
}

function objectIconMarkup(kind) {
  const icons = {
    chart: `<rect x="3" y="4" width="18" height="16" rx="2"></rect><path d="m6 16 4-5 3 3 5-7"></path>`,
    watchlist: `<path d="M8 6h12M8 12h12M8 18h12"></path><path d="M4 6h.01M4 12h.01M4 18h.01"></path>`,
    pane: `<rect x="3" y="4" width="18" height="16" rx="2"></rect><path d="M3 13h18"></path>`,
    group: `<path d="M4 7h6l2 2h8v9H4z"></path>`,
    indicator: `<path d="M3 15c3-8 5 2 8-5s5 5 10-3"></path>`,
    drawing: `<path d="M4 19 19 4"></path><circle cx="7" cy="16" r="2"></circle><circle cx="16" cy="7" r="2"></circle>`,
    alert: `<path d="M6 16.5h12l-1.4-2V10a4.6 4.6 0 0 0-9.2 0v4.5L6 16.5Z"></path><path d="M10 19h4"></path>`,
    utility: `<rect x="4" y="4" width="16" height="16" rx="2"></rect><path d="M8 9h8M8 13h8M8 17h5"></path>`
  };
  return `<svg viewBox="0 0 24 24" aria-hidden="true">${icons[kind] || icons.group}</svg>`;
}
